import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSocket } from './SocketContext';
import apiService from '../services/api';

interface OnlineUsersContextType {
  onlineUsers: Set<string>;
  isUserOnline: (userId: string) => boolean;
  fetchOnlineStatus: () => Promise<void>;
}

const OnlineUsersContext = createContext<OnlineUsersContextType | undefined>(undefined);

export const useOnlineUsers = () => {
  const context = useContext(OnlineUsersContext);
  if (context === undefined) {
    throw new Error('useOnlineUsers must be used within an OnlineUsersProvider');
  }
  return context;
};

export const OnlineUsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const { socket, isConnected } = useSocket();

  const fetchOnlineStatus = async () => {
    try {
      console.log('OnlineUsersContext: Fetching online status...');
      const response = await apiService.getOnlineStatus();
      if (response.success && response.data) {
        const onlineUserIds = Object.keys(response.data).filter(userId => response.data![userId]);
        setOnlineUsers(new Set(onlineUserIds));
        console.log('OnlineUsersContext: Online users updated:', onlineUserIds);
      }
    } catch (error) {
      console.error('OnlineUsersContext: Error fetching online status:', error);
    }
  };

  useEffect(() => {
    if (!socket || !isConnected) return;

    // Fetch initial online status when socket connects
    fetchOnlineStatus();

    const handleOnline = (userId: string) => {
      console.log('OnlineUsersContext: User online:', userId);
      setOnlineUsers(prev => new Set(prev).add(userId));
    };
    
    const handleOffline = (userId: string) => {
      console.log('OnlineUsersContext: User offline:', userId);
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    };

    socket.on('user_online', handleOnline);
    socket.on('user_offline', handleOffline);

    return () => {
      socket.off('user_online', handleOnline);
      socket.off('user_offline', handleOffline);
    };
  }, [socket, isConnected]);

  const isUserOnline = (userId: string) => {
    return onlineUsers.has(userId);
  };

  const value = {
    onlineUsers,
    isUserOnline,
    fetchOnlineStatus,
  };

  return (
    <OnlineUsersContext.Provider value={value}>
      {children}
    </OnlineUsersContext.Provider>
  );
}; 